import { Injectable, NgZone } from '@angular/core';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { PosthogService } from './posthog.service';
import { LocalStorageKeys } from '../helpers/constants';

/**
 * Google Identity Services (GIS) Authentication Service
 * 
 * This service implements Google Sign-In using Google Identity Services,
 * which is the modern replacement for the deprecated Google Sign-In library.
 * 
 * Key features:
 * - One Tap Sign-In: Shows a native-looking prompt in the page (no popup)
 * - Sign-In Button with FedCM: Uses Google's federated credential management
 * - Redirect Flow: Fallback for when FedCM isn't available
 * 
 * Benefits over the old popup-based approach:
 * - No popup blockers
 * - Works with Safari ITP and Chrome's third-party cookie deprecation
 * - Better UX - feels native to the browser
 * - Higher conversion rates (Google reports 195% increase in signups)
 */

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleIdConfig) => void;
          prompt: (callback?: (notification: PromptNotification) => void) => void;
          renderButton: (element: HTMLElement, config: ButtonConfig) => void;
          disableAutoSelect: () => void;
          cancel: () => void;
          revoke: (email: string, callback?: () => void) => void;
          storeCredential: (credential: StoredCredential, callback?: () => void) => void;
        };
        oauth2: {
          initCodeClient: (config: CodeClientConfig) => CodeClient;
          initTokenClient: (config: TokenClientConfig) => TokenClient;
        };
      };
    };
    handleGoogleCredentialResponse?: (response: CredentialResponse) => void;
  }
}

interface GoogleIdConfig {
  client_id: string;
  callback: (response: CredentialResponse) => void;
  auto_select?: boolean;
  login_uri?: string;
  native_callback?: (response: any) => void;
  cancel_on_tap_outside?: boolean;
  prompt_parent_id?: string;
  nonce?: string;
  context?: 'signin' | 'signup' | 'use';
  state_cookie_domain?: string;
  ux_mode?: 'popup' | 'redirect';
  allowed_parent_origin?: string | string[];
  intermediate_iframe_close_callback?: () => void;
  itp_support?: boolean;
  use_fedcm_for_prompt?: boolean;
}

interface ButtonConfig {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number;
  locale?: string;
  click_listener?: () => void;
}

interface PromptNotification {
  isNotDisplayed(): boolean;
  isSkippedMoment(): boolean;
  isDismissedMoment(): boolean;
  isDisplayed(): boolean;
  getNotDisplayedReason(): string;
  getSkippedReason(): string;
  getDismissedReason(): string;
  getMomentType(): string;
}

interface CredentialResponse {
  credential: string;  // JWT ID Token
  select_by: string;
  clientId?: string;
}

interface StoredCredential {
  id: string;
  password: string;
}

interface CodeClientConfig {
  client_id: string;
  scope: string;
  callback: (response: any) => void;
  ux_mode?: 'popup' | 'redirect';
  redirect_uri?: string;
  state?: string;
  select_account?: boolean;
}

interface TokenClientConfig {
  client_id: string;
  scope: string;
  callback: (response: any) => void;
}

interface CodeClient {
  requestCode: () => void;
}

interface TokenClient {
  requestAccessToken: () => void;
}

export interface GoogleUser {
  id: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  photoUrl: string;
  provider: string;
  idToken: string;
  authToken?: string;
}

export interface GoogleAuthError {
  type: 'one_tap_failed' | 'button_failed' | 'redirect_failed' | 'token_invalid' | 'script_load_failed' | 'user_cancelled';
  reason: string;
  originalError?: any;
}

@Injectable({
  providedIn: 'root'
})
export class GoogleAuthService {
  private readonly CLIENT_ID = '991481757079-md18668odeqpcv9mgp2l33qb204i2jc3.apps.googleusercontent.com';
  private readonly SCRIPT_URL = 'https://accounts.google.com/gsi/client';
  
  private scriptLoaded = false;
  private scriptLoading = false;
  private initializationAttempted = false;
  
  // Auth state observables
  private authStateSubject = new BehaviorSubject<GoogleUser | null>(null);
  private readyStateSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new Subject<GoogleAuthError>();
  
  public authState$: Observable<GoogleUser | null> = this.authStateSubject.asObservable();
  public ready$: Observable<boolean> = this.readyStateSubject.asObservable();
  public error$: Observable<GoogleAuthError> = this.errorSubject.asObservable();

  constructor(
    private ngZone: NgZone,
    private posthog: PosthogService
  ) {
    console.log('[GoogleAuthService] Service instantiated');
    
    // Set up global callback for credential response
    window.handleGoogleCredentialResponse = (response: CredentialResponse) => {
      this.handleCredentialResponse(response);
    };
  }

  /**
   * Initialize the Google Identity Services
   * Must be called before using any authentication features
   */
  async initialize(): Promise<boolean> {
    console.log('[GoogleAuthService] initialize() called');
    
    if (this.initializationAttempted) {
      console.log('[GoogleAuthService] Already initialized, returning current state');
      return this.readyStateSubject.value;
    }
    
    this.initializationAttempted = true;
    
    try {
      await this.loadGoogleScript();
      
      if (!window.google?.accounts?.id) {
        throw new Error('Google Identity Services not available after script load');
      }
      
      // Initialize Google ID
      window.google.accounts.id.initialize({
        client_id: this.CLIENT_ID,
        callback: (response: CredentialResponse) => this.handleCredentialResponse(response),
        auto_select: false, // Don't auto-select to give user control
        cancel_on_tap_outside: true,
        use_fedcm_for_prompt: true, // Use FedCM when available (modern browsers)
        itp_support: true, // Enable Safari ITP support
        context: 'signin',
        ux_mode: 'popup', // We'll fall back to redirect if needed
      });
      
      console.log('[GoogleAuthService] Google Identity Services initialized successfully');
      this.readyStateSubject.next(true);
      
      this.posthog.captureEvent('google_auth_initialized', {
        method: 'gis',
        fedcm_supported: this.isFedCMSupported(),
        itp_support: true,
      });
      
      // Check for returning user auto-login
      this.checkAutoLogin();
      
      return true;
    } catch (error) {
      console.error('[GoogleAuthService] Initialization failed:', error);
      this.errorSubject.next({
        type: 'script_load_failed',
        reason: error.message || 'Failed to load Google Identity Services',
        originalError: error
      });
      
      this.posthog.captureEvent('google_auth_init_failed', {
        error_message: error.message,
        error_type: 'initialization_error',
      });
      
      return false;
    }
  }

  /**
   * Load the Google Identity Services script
   */
  private loadGoogleScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.scriptLoaded && window.google?.accounts?.id) {
        console.log('[GoogleAuthService] Script already loaded');
        resolve();
        return;
      }
      
      if (this.scriptLoading) {
        console.log('[GoogleAuthService] Script already loading, waiting...');
        // Wait for existing load to complete
        const checkInterval = setInterval(() => {
          if (window.google?.accounts?.id) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
        
        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error('Script load timeout'));
        }, 10000);
        return;
      }
      
      this.scriptLoading = true;
      console.log('[GoogleAuthService] Loading Google Identity Services script');
      
      // Check if script already exists
      const existingScript = document.querySelector(`script[src="${this.SCRIPT_URL}"]`);
      if (existingScript) {
        console.log('[GoogleAuthService] Script tag already exists, waiting for load...');
        if (window.google?.accounts?.id) {
          this.scriptLoaded = true;
          this.scriptLoading = false;
          resolve();
          return;
        }
        
        existingScript.addEventListener('load', () => {
          this.scriptLoaded = true;
          this.scriptLoading = false;
          resolve();
        });
        
        existingScript.addEventListener('error', (error) => {
          this.scriptLoading = false;
          reject(new Error('Failed to load Google script'));
        });
        return;
      }
      
      // Create and load script
      const script = document.createElement('script');
      script.src = this.SCRIPT_URL;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        console.log('[GoogleAuthService] Script loaded successfully');
        this.scriptLoaded = true;
        this.scriptLoading = false;
        
        // Small delay to ensure google object is fully available
        setTimeout(() => {
          if (window.google?.accounts?.id) {
            resolve();
          } else {
            reject(new Error('Google Identity Services not available after load'));
          }
        }, 100);
      };
      
      script.onerror = (error) => {
        console.error('[GoogleAuthService] Script load error:', error);
        this.scriptLoading = false;
        reject(new Error('Failed to load Google Identity Services script'));
      };
      
      document.head.appendChild(script);
    });
  }

  /**
   * Show the One Tap prompt
   * This is the most conversion-friendly method as it shows a non-intrusive prompt
   */
  showOneTap(): void {
    console.log('[GoogleAuthService] showOneTap() called');
    
    if (!this.readyStateSubject.value) {
      console.error('[GoogleAuthService] Service not ready, initializing first');
      this.initialize().then(() => {
        if (this.readyStateSubject.value) {
          this.showOneTap();
        }
      });
      return;
    }
    
    this.posthog.captureEvent('one_tap_prompt_requested');
    
    try {
      window.google?.accounts.id.prompt((notification: PromptNotification) => {
        this.ngZone.run(() => {
          this.handlePromptNotification(notification);
        });
      });
    } catch (error) {
      console.error('[GoogleAuthService] Error showing One Tap prompt:', error);
      this.posthog.captureEvent('one_tap_prompt_error', {
        error_message: error.message,
      });
    }
  }

  /**
   * Handle One Tap prompt notification
   */
  private handlePromptNotification(notification: PromptNotification): void {
    console.log('[GoogleAuthService] Prompt notification received');
    
    if (notification.isNotDisplayed()) {
      const reason = notification.getNotDisplayedReason();
      console.log('[GoogleAuthService] Prompt not displayed, reason:', reason);
      
      this.posthog.captureEvent('one_tap_not_displayed', {
        reason: reason,
      });
      
      // Handle specific reasons
      if (reason === 'suppressed_by_user' || reason === 'opt_out_or_no_session') {
        // User has previously dismissed One Tap, don't show again
        console.log('[GoogleAuthService] User has opted out of One Tap');
      }
    } else if (notification.isSkippedMoment()) {
      const reason = notification.getSkippedReason();
      console.log('[GoogleAuthService] Prompt skipped, reason:', reason);
      
      this.posthog.captureEvent('one_tap_skipped', {
        reason: reason,
      });
    } else if (notification.isDismissedMoment()) {
      const reason = notification.getDismissedReason();
      console.log('[GoogleAuthService] Prompt dismissed, reason:', reason);
      
      this.posthog.captureEvent('one_tap_dismissed', {
        reason: reason,
      });
      
      if (reason === 'credential_returned') {
        // Credential was returned, auth will be handled by callback
        console.log('[GoogleAuthService] Credential returned from One Tap');
      }
    } else if (notification.isDisplayed()) {
      console.log('[GoogleAuthService] Prompt is displayed');
      this.posthog.captureEvent('one_tap_displayed');
    }
  }

  /**
   * Render the Google Sign-In button in a container element
   * This uses Google's official button which handles all the UI and auth flow
   */
  renderButton(elementId: string, options?: Partial<ButtonConfig>): void {
    console.log('[GoogleAuthService] renderButton() called for element:', elementId);
    
    if (!this.readyStateSubject.value) {
      console.log('[GoogleAuthService] Service not ready, will render when ready');
      
      const subscription = this.ready$.subscribe(ready => {
        if (ready) {
          subscription.unsubscribe();
          this.renderButton(elementId, options);
        }
      });
      return;
    }
    
    const element = document.getElementById(elementId);
    if (!element) {
      console.error('[GoogleAuthService] Element not found:', elementId);
      return;
    }
    
    // Clear any existing content
    element.innerHTML = '';
    
    const defaultOptions: ButtonConfig = {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
      logo_alignment: 'left',
      width: 320,
      click_listener: () => {
        console.log('[GoogleAuthService] Google button clicked');
        this.posthog.captureEvent('google_button_clicked', {
          method: 'gis_button',
        });
      }
    };
    
    const buttonOptions = { ...defaultOptions, ...options };
    
    try {
      window.google?.accounts.id.renderButton(element, buttonOptions);
      console.log('[GoogleAuthService] Button rendered successfully');
      
      this.posthog.captureEvent('google_button_rendered', {
        button_type: buttonOptions.type,
        button_theme: buttonOptions.theme,
        button_size: buttonOptions.size,
      });
    } catch (error) {
      console.error('[GoogleAuthService] Error rendering button:', error);
      this.posthog.captureEvent('google_button_render_error', {
        error_message: error.message,
      });
    }
  }

  /**
   * Handle the credential response from Google
   * This is called when the user successfully authenticates
   */
  private handleCredentialResponse(response: CredentialResponse): void {
    console.log('[GoogleAuthService] Credential response received:', {
      select_by: response.select_by,
      hasCredential: !!response.credential,
    });
    
    this.ngZone.run(() => {
      try {
        // Decode the JWT to get user info
        const user = this.decodeJwtToken(response.credential);
        
        console.log('[GoogleAuthService] User authenticated:', {
          email: user.email,
          name: user.name,
          select_by: response.select_by,
        });
        
        // Store auth provider for auto-login on return
        localStorage.setItem(LocalStorageKeys.AuthProvider, 'GOOGLE');
        localStorage.setItem('gis_id_token', response.credential);
        
        // Emit the auth state
        this.authStateSubject.next(user);
        
        // Track successful login
        this.posthog.captureEvent('login_successful', {
          method: 'google_gis',
          select_by: response.select_by,
          fedcm_used: response.select_by?.includes('fedcm') || false,
        });
        
      } catch (error) {
        console.error('[GoogleAuthService] Error processing credential:', error);
        
        this.errorSubject.next({
          type: 'token_invalid',
          reason: 'Failed to decode authentication token',
          originalError: error
        });
        
        this.posthog.captureEvent('login_failed', {
          method: 'google_gis',
          error_type: 'token_decode_failed',
          error_message: error.message,
        });
      }
    });
  }

  /**
   * Decode JWT token to extract user information
   */
  private decodeJwtToken(token: string): GoogleUser {
    try {
      // JWT is in format: header.payload.signature
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      const payload = JSON.parse(jsonPayload);
      
      return {
        id: payload.sub,
        email: payload.email,
        name: payload.name,
        firstName: payload.given_name || '',
        lastName: payload.family_name || '',
        photoUrl: payload.picture || '',
        provider: 'GOOGLE',
        idToken: token,
      };
    } catch (error) {
      console.error('[GoogleAuthService] JWT decode error:', error);
      throw new Error('Failed to decode JWT token');
    }
  }

  /**
   * Check for returning user and attempt auto-login
   */
  private checkAutoLogin(): void {
    const authProvider = localStorage.getItem(LocalStorageKeys.AuthProvider);
    const storedToken = localStorage.getItem('gis_id_token');
    
    if (authProvider === 'GOOGLE' && storedToken) {
      console.log('[GoogleAuthService] Found stored credentials, attempting auto-login');
      
      try {
        // Verify token is still valid (not expired)
        const payload = JSON.parse(atob(storedToken.split('.')[1]));
        const expiry = payload.exp * 1000; // Convert to milliseconds
        
        if (Date.now() < expiry) {
          console.log('[GoogleAuthService] Stored token is valid, restoring session');
          const user = this.decodeJwtToken(storedToken);
          this.authStateSubject.next(user);
          
          this.posthog.captureEvent('auto_login_success', {
            method: 'google_gis',
          });
        } else {
          console.log('[GoogleAuthService] Stored token expired, clearing');
          localStorage.removeItem('gis_id_token');
          
          // Show One Tap to make re-login easy
          setTimeout(() => this.showOneTap(), 1000);
        }
      } catch (error) {
        console.error('[GoogleAuthService] Error checking stored credentials:', error);
        localStorage.removeItem('gis_id_token');
      }
    }
  }

  /**
   * Sign out the current user
   */
  signOut(): void {
    console.log('[GoogleAuthService] signOut() called');
    
    const currentUser = this.authStateSubject.value;
    
    if (currentUser?.email) {
      // Revoke Google's credential
      window.google?.accounts.id.revoke(currentUser.email, () => {
        console.log('[GoogleAuthService] Google credential revoked');
      });
    }
    
    // Disable auto-select for next login
    window.google?.accounts.id.disableAutoSelect();
    
    // Clear stored credentials
    localStorage.removeItem(LocalStorageKeys.AuthProvider);
    localStorage.removeItem('gis_id_token');
    
    // Clear auth state
    this.authStateSubject.next(null);
    
    this.posthog.captureEvent('logout', {
      method: 'google_gis',
    });
  }

  /**
   * Get the current authenticated user
   */
  getCurrentUser(): GoogleUser | null {
    return this.authStateSubject.value;
  }

  /**
   * Check if the user is authenticated
   */
  isAuthenticated(): boolean {
    return this.authStateSubject.value !== null;
  }

  /**
   * Check if FedCM is supported in the current browser
   */
  private isFedCMSupported(): boolean {
    return 'IdentityCredential' in window || 'FederatedCredential' in window;
  }

  /**
   * Cancel any pending One Tap prompt
   */
  cancelOneTap(): void {
    window.google?.accounts.id.cancel();
  }

  /**
   * Get service ready state
   */
  isReady(): boolean {
    return this.readyStateSubject.value;
  }
}

